import { AppBar as MuiAppBar, Box, Toolbar, Typography } from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate, useSearchParams } from 'react-router';
import {
  LUNA_TELEMETRY_PATH,
  SDLC_PATH,
  SEARCH_PATH,
  SEARCH_QUERY_PARAM,
  TOOLKIT_PATH,
  config,
} from '../../config';
import { contentWidthSx, pageGutterSx, tokens } from '../../theme';
import { ActionPill } from '../ActionPill';
import { Logo } from '../Logo';
import { SearchField } from '../SearchField';
import { ThemeModeToggle } from '../ThemeModeToggle';
import type { AppBarProps, NavItem } from './types';

/**
 * The bar across the top of every authenticated page.
 *
 * **The mark sits left of the wordmark, which the designs do not show** — the
 * Figma frames carry no logo anywhere. Together the two are one link home; a
 * logo that is not a home link is a decoration.
 */
const navItems = (sdlcAccess: boolean): NavItem[] => [
  // Omitted rather than drawn disabled when access is missing — hiding a nav
  // entry entirely is what stops a click landing on a guard redirect.
  ...(sdlcAccess ? [{ label: 'SDLC', to: SDLC_PATH }] : []),
  { label: 'Toolkit', to: TOOLKIT_PATH },
  { label: 'Luna Telemetry', to: LUNA_TELEMETRY_PATH },
];

const APP_BAR_HEIGHT = 72;

/**
 * A real `<a>` wrapping its content, rather than MUI's polymorphic `component`
 * prop — which puts MUI's generics between the element and its keyboard and
 * screen-reader behaviour for no gain.
 */
const bareLinkStyle = { textDecoration: 'none', color: 'inherit' } as const;

export const AppBar = ({ userLabel, sdlcAccess, onSignOut }: AppBarProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [params] = useSearchParams();

  const isSearching = pathname === SEARCH_PATH;

  /**
   * Submitting keeps the rest of the URL when already on the results page, so
   * refining the words does not drop the ticked filters — and starts clean from
   * anywhere else, where the current query string is unrelated to a search.
   */
  const runSearch = (query: string) => {
    const next = isSearching ? new URLSearchParams(params) : new URLSearchParams();

    if (query === '') {
      next.delete(SEARCH_QUERY_PARAM);
    } else {
      next.set(SEARCH_QUERY_PARAM, query);
    }

    void navigate({ pathname: SEARCH_PATH, search: next.toString() });
  };

  return (
    <MuiAppBar
      position="sticky"
      elevation={0}
      // Not `color="default"`: MUI's default paints grey. The bar is the page's
      // own surface with a rule under it, which is what the designs draw.
      sx={{
        backgroundColor: 'surface.page',
        color: 'text.primary',
        borderBottom: 1,
        borderColor: 'border.default',
      }}
    >
      <Toolbar disableGutters sx={pageGutterSx}>
        <Box
          sx={{
            ...contentWidthSx,
            minHeight: APP_BAR_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <RouterLink to={sdlcAccess ? SDLC_PATH : TOOLKIT_PATH} style={bareLinkStyle}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
              {/* No label: the wordmark beside it already names the product, and
                  two accessible names on one link reads it twice. */}
              <Logo size={28} />
              <Typography
                variant="h3"
                component="span"
                sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
              >
                {config.appName}
              </Typography>
            </Box>
          </RouterLink>

          <Box
            component="nav"
            aria-label="Main"
            sx={{
              // No mobile design exists for the bar. Hiding the tabs below
              // tablet keeps the logo and actions usable.
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              gap: 4,
              ml: 4,
            }}
          >
            {navItems(sdlcAccess).map((item) => (
              <NavTab key={item.label} item={item} pathname={pathname} />
            ))}
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {/*
            **The field replaces the pill's magnifier from tablet up**, rather
            than sitting beside it. The designs draw both, which is two controls
            for one action on the same bar; the icon survives only below tablet,
            where a 300px field does not fit and a button to a page that has one
            is the honest substitute.

            `minWidth: 0` so it gives way to the nav and the actions rather than
            pushing them off the bar at the narrow end of tablet.
          */}
          {sdlcAccess ? (
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexShrink: 1, minWidth: 0, mr: 4 }}>
              <SearchField
                value={isSearching ? (params.get(SEARCH_QUERY_PARAM) ?? '') : ''}
                onSubmit={runSearch}
              />
            </Box>
          ) : null}

          {/* Beside the account actions rather than inside them: the pill is
              what you do with your session, and this is what the page looks
              like. One bar covers every authenticated page. */}
          <Box sx={{ mr: 2 }}>
            <ThemeModeToggle />
          </Box>

          <ActionPill
            userLabel={userLabel}
            onSearch={sdlcAccess ? () => { void navigate(SEARCH_PATH); } : undefined}
            onSignOut={onSignOut}
          />
        </Box>
      </Toolbar>
    </MuiAppBar>
  );
};

/**
 * One tab. Active when the current path is inside its section, so a method page
 * three levels down still shows SDLC as the section you are in.
 */
const NavTab = ({ item, pathname }: { item: NavItem; pathname: string }) => {
  const { to } = item;

  if (to === undefined) {
    return (
      <Typography
        variant="body2"
        component="span"
        // Not a button and not focusable. `aria-disabled` on something you can
        // still tab to is a promise the keyboard does not keep.
        aria-disabled
        sx={{ ...tabSx, color: 'text.disabled', cursor: 'default' }}
      >
        {item.label}
      </Typography>
    );
  }

  const isActive = pathname.startsWith(to);

  return (
    <RouterLink to={to} style={bareLinkStyle} aria-current={isActive ? 'page' : undefined}>
      <Typography
        variant="body2"
        component="span"
        sx={{
          ...tabSx,
          color: isActive ? 'text.primary' : 'text.secondary',
          fontWeight: isActive ? 500 : 400,
          // surface.sunken, not surface.raised: the AppBar itself paints
          // surface.page, and the two are the same white since this fix's
          // token flattening — a raised-coloured pill would be invisible.
          '&:hover': { color: 'text.primary', backgroundColor: 'surface.sunken' },
          // The active underline, drawn as a pseudo-element so it sits under the
          // label rather than under the hover pill's padding.
          '&::after': isActive
            ? {
                content: '""',
                position: 'absolute',
                left: 8,
                right: 8,
                bottom: 2,
                height: 2,
                backgroundColor: 'primary.main',
              }
            : undefined,
        }}
      >
        {item.label}
      </Typography>
    </RouterLink>
  );
};

const tabSx = {
  position: 'relative',
  display: 'inline-block',
  paddingInline: 2,
  paddingBlock: 1,
  borderRadius: `${String(tokens.radius.md)}px`,
  whiteSpace: 'nowrap',
} as const;
